import { serverTimestamp } from 'firebase/database';

import { getFirebaseApp, getAuthData } from '../katapult-job/KatapultJobUtilities.js';
import { composeCompaniesWithAccessUpdate, composeModelPathUpdate } from './KatapultModelUpdateComposers.js';
import { getAppTypeDisplayName } from './KatapultModelUtilities.js';
import { getClientOrAdminStorage } from '../firebase-helpers/ClientOrAdminStorage.js';
import { ShallowGet } from '../../modules/ShallowGet.js';
import { FirebaseConfig } from '../../modules/FirebaseConfig.js';
import { LargeSet } from '../../modules/LargeSet.js';
import { isFlagEnabled } from '../../modules/FeatureFlags.js';

const SNAPSHOT_STORAGE_ROOT = 'model_snapshots';
const SNAPSHOT_FILE_ROOT = 'models';

/** @import { ClientOrAdminFirebase } from '../types/ClientOrAdminFirebase.js' */

/**
 * @template T - The type of data to be returned from a snapshot.
 * @callback OnSnapshotCustomCallback<T> Function signature for a custom snapshot callback.
 * @param {{ val: () => T }} snapshot - The snapshot of the data.
 * @returns {void}
 */

/**
 * @import { OnSnapshotCallback, ModelAttribute } from './KatapultModelTypes.d.ts';
 */

/**
 * @typedef {Object} KatapultModelOptions Options for the model object.
 * @property {ClientOrAdminFirebase} [firebaseApp] - The Firebase app to use for backend operations.
 * @property {boolean} [autoCache] - Automatically cache data on "gets" (use in conjunction with {@link KatapultModel.destroy} to remove
 * listeners).
 */

/**
 * @typedef {Object} ModelSpaceData A model space's data, keyed as it is in the database.
 * @property {object} models - The model data.
 * @property {object} [portal_config] - The workflow config.
 */

/**
 * @typedef {Object} SnapshotMetadata
 * @property {string} id - The unique identifier for the snapshot.
 * @property {string} name - The name of the snapshot.
 * @property {string} model_id - The id of the model the snapshot belongs to.
 * @property {string} storage_path - The storage path where the snapshot data is stored.
 * @property {number} created_at - The timestamp when the snapshot was created.
 * @property {string} created_by_uid - The uid of the user who created the snapshot.
 * @property {number} size_bytes - The size of the snapshot data in bytes.
 */

export class KatapultModel {
  static MODEL_COMPANY_SPACE_PATHS = [
    'companies_with_model_access',
    'companies_with_use_model_access',
    'companies_with_write_model_access',
    'date_created',
    'model_options',
    'model_owner',
    'models',
    'name',
    'portal_config',
    'subscription/models_only',
    'tier'
  ];

  static MODEL_DATA_PATHS = [
    'action_tracking',
    'alternate_designs',
    'api_payload',
    // 'app_picklist_backup', // deprecated
    'application_companies',
    'attribute_groups',
    'attribute_sets',
    'attributes',
    'button_groups',
    'collection_order_logic',
    'compatible_unit_entry',
    'compatible_unit_models',
    // 'compatible_units', // deprecated - replaced by compatible_unit_models/cus
    'config',
    'construction_specs',
    'custom_icon_collections',
    'custom_icons',
    'custom_line_styles',
    'drawing_disallowed_attachments',
    'export_models',
    'files',
    'hw_details_options',
    'image_recognition_models',
    'import_models',
    'input_model_groups',
    'input_models',
    'job_tracker',
    // 'kpla_test_importer', // internal only
    'make_ready',
    'map_print_annotation_logic',
    'map_styles',
    'mapping_buttons',
    // 'mobile_selection_parameters', // deprecated
    // 'mr_clearances', // deprecated
    'mr_preferences',
    'njuns_models',
    'overhead_time_allocations',
    // 'pe_review_sampling', // deprecated
    'pole_loading',
    'pole_replacement_dialog',
    'power_specs',
    // 'proficiency_report', // deprecated
    'project_config',
    // 'projects', // deprecated
    'proposed_sag_data',
    'qc',
    'quality_control',
    // 'report_card', // deprecated
    'review_sampling',
    'roles',
    'routines',
    'sag_data',
    'saved_views',
    'shortcuts',
    'snapshot_models',
    'tags',
    'trace_models'
    // 'user_time_allocations' // deprecated
  ];

  // Portal config keys left untouched when writing an imported or restored workflow config.
  static PORTAL_CONFIG_KEYS_TO_IGNORE = ['app_number_counter', 'app_draft_number_counter', 'sync_to_models', 'synced_from'];

  /**
   * The id (key) of the model.
   * @type {string}
   */
  #id;

  /**
   * Auto-cache model data.
   * @type {boolean}
   */
  #autoCache = false;

  /**
   * Record of snapshot listener lists by path.
   * @type {Record<string, OnSnapshotCallback[]>}
   */
  #listenersByPath = {};

  /**
   * Record of caching listeners by path.
   * @type {Record<string, OnSnapshotCallback>}
   */
  #cachingListenersByPath = {};

  /**
   * A Katapult Pro model data object.
   * @param {string} modelId - The id (key) of the model.
   * @param {KatapultModelOptions} options - Options for the data object.
   */
  constructor(modelId, options = {}) {
    this.#id = modelId;
    this.firebase = options?.firebaseApp ?? getFirebaseApp();
    this.#autoCache = options.autoCache ?? false;
    this.database = this.firebase.database();
    this.modelPath = `photoheight/company_space/${modelId}`;
    this.modelDataPath = `${this.modelPath}/models`;
    this.reference = this.database.ref(this.modelPath);
  }

  /**
   * The id (key) of the model.
   * @type {string}
   */
  get id() {
    return this.#id;
  }

  /**
   * Whether auto-caching listeners will be set up on data "gets".
   * @type {boolean}
   */
  get autoCache() {
    return this.#autoCache;
  }

  /**
   * Get a new Katapult Model object from a model id.
   * @param {string} modelId - The id (key) of the model.
   * @param {KatapultModelOptions} [options] - Options for the model object.
   * @returns {KatapultModel} The model object.
   */
  static fromId(modelId, options) {
    return new KatapultModel(modelId, options);
  }

  /**
   * Create a new model.
   * @param {string} modelId - The id (key) of the model.
   * @returns {Promise<KatapultModel>} The created model.
   */
  static async create(modelId) {
    throw new Error('create not yet implemented');
  }

  // Returns a method which updates a given stage and then reports the new total progress
  static trackProgress(stages, onProgress) {
    return (key, current, total) => {
      if (current != null) stages[key].current = current;
      if (total != null) stages[key].total = total;
      const sum = (property) => Object.values(stages).reduce((acc, stage) => acc + stage[property], 0);
      onProgress?.({ current: sum('current'), total: sum('total') });
    };
  }

  /**
   * Destroy the model object, removing all listeners.
   * @returns {void}
   */
  destroy() {
    // Remove all listeners for each path.
    const listenersByPathEntries = Object.entries(this.#listenersByPath);
    for (const [path, listeners] of listenersByPathEntries) {
      for (const listener of listeners) {
        this.reference.child(path).off('value', listener);
      }
    }

    // Remove all caching listeners.
    const cachingListenersByPathEntries = Object.entries(this.#cachingListenersByPath);
    for (const [path, listener] of cachingListenersByPathEntries) {
      this.reference.child(path).off('value', listener);
    }

    // Clear the listener paths.
    this.#listenersByPath = {};
    this.#cachingListenersByPath = {};
  }

  /**
   * Get the companies with read access to the model.
   * @return {Promise<object>} An object where the keys are companyIds and the values are booleans representing whether the company has read access to the model.
   */
  async getCompaniesWithReadAccess() {
    return await this.#readDataOnce('companies_with_model_access');
  }

  /**
   * Get whether a specific company has read access to the model.
   * @param {string} companyId - The company ID to check.
   * @return {Promise<true|null>} The raw Firebase value — `true` if the company has read access, `null` otherwise.
   */
  async getCompanyReadAccess(companyId) {
    return await this.#readDataOnce(`companies_with_model_access/${companyId}`);
  }

  /**
   * Listen for changes to the companies with read access to the model.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the companies with read access to the model change.
   * @returns {() => void} A function to remove the listener.
   */
  onCompaniesWithReadAccessSnapshot(callback) {
    return this.#setUpListener('companies_with_model_access', callback);
  }

  /**
   * Set the companies with read access to the model.
   * @param {object} companiesAccessObject - An object where the keys are companyIds and the values are booleans representing whether the company should have read access to the model.
   * @returns {Promise<void>} A promise that resolves when the companies with read access have been set.
   */
  async setCompaniesWithReadAccess(companiesAccessObject) {
    // Validate the companiesAccessObject
    if (
      companiesAccessObject === null ||
      typeof companiesAccessObject !== 'object' ||
      Array.isArray(companiesAccessObject) ||
      Object.getPrototypeOf(companiesAccessObject) !== Object.prototype
    ) {
      throw new Error('Invalid companies access object: expected a plain object mapping companyId to boolean');
    }
    for (const [companyId, hasAccess] of Object.entries(companiesAccessObject)) {
      if (typeof hasAccess !== 'boolean') {
        throw new Error(`Invalid companies access object: value for companyId "${companyId}" must be a boolean`);
      }
    }
    // Check to see if the user can modify the model access
    const modelOwner = await this.getModelOwner();

    const { root_company: userRootCompany, user_group: userActiveCompany } = (await getAuthData(this.firebase)) ?? {};
    if (userRootCompany !== modelOwner && userActiveCompany !== modelOwner) throw new Error(`Only the model owner may modify model access`);

    // Create an update object and make the update
    const update = composeCompaniesWithAccessUpdate(companiesAccessObject, { modelId: this.id, type: 'read' });

    // Perform the update
    return await this.reference.update(update);
  }

  /**
   * Get the companies with use access to the model.
   * @return {Promise<object>} An object where the keys are companyIds and the values are booleans representing whether the company has use access to the model.
   */
  async getCompaniesWithUseAccess() {
    return await this.#readDataOnce('companies_with_use_model_access');
  }

  /**
   * Get whether a specific company has use access to the model.
   * @param {string} companyId - The company ID to check.
   * @return {Promise<true|null>} The raw Firebase value — `true` if the company has use access, `null` otherwise.
   */
  async getCompanyUseAccess(companyId) {
    return await this.#readDataOnce(`companies_with_use_model_access/${companyId}`);
  }

  /**
   * Listen for changes to the companies with use access to the model.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the companies with use access to the model change.
   * @returns {() => void} A function to remove the listener.
   */
  onCompaniesWithUseAccessSnapshot(callback) {
    return this.#setUpListener('companies_with_use_model_access', callback);
  }

  /**
   * Set the companies with use access to the model.
   * @param {object} companiesAccessObject - An object where the keys are companyIds and the values are booleans representing whether the company should have use access to the model.
   * @returns {Promise<void>} A promise that resolves when the companies with use access have been set.
   */
  async setCompaniesWithUseAccess(companiesAccessObject) {
    // Validate the companiesAccessObject
    if (
      companiesAccessObject === null ||
      typeof companiesAccessObject !== 'object' ||
      Array.isArray(companiesAccessObject) ||
      Object.getPrototypeOf(companiesAccessObject) !== Object.prototype
    ) {
      throw new Error('Invalid companies access object: expected a plain object mapping companyId to boolean');
    }
    for (const [companyId, hasAccess] of Object.entries(companiesAccessObject)) {
      if (typeof hasAccess !== 'boolean') {
        throw new Error(`Invalid companies access object: value for companyId "${companyId}" must be a boolean`);
      }
    }
    // Check to see if the user can modify the model access
    const modelOwner = await this.getModelOwner();

    const { root_company: userRootCompany, user_group: userActiveCompany } = (await getAuthData(this.firebase)) ?? {};
    if (userRootCompany !== modelOwner && userActiveCompany !== modelOwner) throw new Error(`Only the model owner may modify model access`);

    // Create an update object and make the update
    const update = composeCompaniesWithAccessUpdate(companiesAccessObject, { modelId: this.id, type: 'use' });

    // Perform the update
    return await this.reference.update(update);
  }

  /**
   * Get the companies with write access to the model.
   * @return {Promise<object>} An object where the keys are companyIds and the values are booleans representing whether the company has write access to the model.
   */
  async getCompaniesWithWriteAccess() {
    return await this.#readDataOnce('companies_with_write_model_access');
  }

  /**
   * Get whether a specific company has write access to the model.
   * @param {string} companyId - The company ID to check.
   * @return {Promise<true|null>} The raw Firebase value — `true` if the company has write access, `null` otherwise.
   */
  async getCompanyWriteAccess(companyId) {
    return await this.#readDataOnce(`companies_with_write_model_access/${companyId}`);
  }

  /**
   * Listen for changes to the companies with write access to the model.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the companies with write access to the model change.
   * @returns {() => void} A function to remove the listener.
   */
  onCompaniesWithWriteAccessSnapshot(callback) {
    return this.#setUpListener('companies_with_write_model_access', callback);
  }

  /**
   * Set the companies with write access to the model.
   * @param {object} companiesAccessObject - An object where the keys are companyIds and the values are booleans representing whether the company should have write access to the model.
   * @returns {Promise<void>} A promise that resolves when the companies with write access have been set.
   */
  async setCompaniesWithWriteAccess(companiesAccessObject) {
    // Validate the companiesAccessObject
    if (
      companiesAccessObject === null ||
      typeof companiesAccessObject !== 'object' ||
      Array.isArray(companiesAccessObject) ||
      Object.getPrototypeOf(companiesAccessObject) !== Object.prototype
    ) {
      throw new Error('Invalid companies access object: expected a plain object mapping companyId to boolean');
    }
    for (const [companyId, hasAccess] of Object.entries(companiesAccessObject)) {
      if (typeof hasAccess !== 'boolean') {
        throw new Error(`Invalid companies access object: value for companyId "${companyId}" must be a boolean`);
      }
    }

    // Check to see if the user can modify the model access
    const modelOwner = await this.getModelOwner();

    const { root_company: userRootCompany, user_group: userActiveCompany } = (await getAuthData(this.firebase)) ?? {};
    if (userRootCompany !== modelOwner && userActiveCompany !== modelOwner) throw new Error(`Only the model owner may modify model access`);

    // Create an update object and make the update
    const update = composeCompaniesWithAccessUpdate(companiesAccessObject, { modelId: this.id, type: 'write' });

    // Perform the update
    return await this.reference.update(update);
  }

  /**
   * Get the date the model was created.
   * @returns {Promise<number>} The date the model was created in milliseconds.
   */
  async getDateCreated() {
    return await this.#readDataOnce('date_created');
  }

  /**
   * Listen for changes to the date the model was created.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the date the model was created changes.
   * @returns {() => void} A function to remove the listener.
   */
  onDateCreatedSnapshot(callback) {
    return this.#setUpListener('date_created', callback);
  }

  /**
   * Set the date the model was created.
   * @param {number} [date] - The date to set for when the model was created in milliseconds (optional). If no date is provided, the date created will be set to the current date and time.
   * @returns {Promise<void>} A promise that resolves when the date the model was created has been set.
   */
  async setDateCreated(date) {
    if (date !== undefined && (typeof date !== 'number' || Number.isNaN(date)))
      throw new Error(`Date must be a valid number of milliseconds since the Unix epoch`);

    return await this.reference.child('date_created').set(date ?? serverTimestamp());
  }

  /**
   * Get the model's owner.
   * @returns {Promise<string>} The model's owner.
   */
  async getModelOwner() {
    return await this.#readDataOnce('model_owner');
  }

  /**
   * Listen for changes to the model's owner.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's owner changes.
   * @returns {() => void} A function to remove the listener.
   */
  onModelOwnerSnapshot(callback) {
    return this.#setUpListener('model_owner', callback);
  }

  /**
   * Set the model's owner.
   * @param {string} owner - The owner to set for the model.
   * @returns {Promise<void>} A promise that resolves when the model's owner has been set.
   */
  async setModelOwner(owner) {
    if (typeof owner !== 'string') throw new Error(`Owner is not a valid string`);
    return await this.reference.child('model_owner').set(owner);
  }

  /**
   * Get the model's name.
   * @returns {Promise<string>} The model's name.
   */
  async getName() {
    return await this.#readDataOnce('name');
  }

  /**
   * Listen for changes to the model's name.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's name changes.
   * @returns {() => void} A function to remove the listener.
   */
  onNameSnapshot(callback) {
    return this.#setUpListener('name', callback);
  }

  /**
   * Set the model's name.
   * @param {string} name - The name to set for the model.
   * @returns {Promise<void>} A promise that resolves when the name has been set.
   */
  async setName(name) {
    throw new Error('setName not yet implemented');
  }

  /**
   * Get the model alias for a company.
   * @param {string} companyId - The id of the company to get the model alias for.
   * @returns {Promise<string|null>} The model alias for the company, or null if it doesn't exist.
   */
  async getModelAliasForCompany(companyId) {
    const modelOptions = await this.#readDataOnce(`photoheight/company_space/${companyId}/model_options`, { absolutePath: true });

    const modelData = modelOptions?.filter((option) => option.value === this.id)?.[0] ?? [];
    return modelData?.label ?? null;
  }

  /**
   * Fetches the model options.
   * @returns {Promise<Array>} The model options for the company
   */
  async getModelOptions() {
    return (await this.#readDataOnce('model_options')) || [];
  }

  /**
   * Set the model alias for a company.
   * @param {string} companyId - The id of the company to set the model alias for.
   * @param {string} alias - The alias to set for the model for the company.
   * @returns {Promise<void>} A promise that resolves when the model alias for the company has been set.
   */
  async setModelAliasForCompany(companyId, alias) {
    throw new Error('setModelAliasForCompany not yet implemented');
  }

  /**
   * Get the model's portal app types.
   * @returns {Promise<Object>} The model's portal app-type configs, keyed by push id.
   */
  async getPortalAppTypes() {
    return await this.#readDataOnce('portal_config/app_types');
  }

  /**
   * Get the display name for one of the model's portal app types.
   * @param {string} appTypeKey - The app-type key stored on a job, either a push id or a snake-case key.
   * @returns {Promise<string>} The app type's display name, or the key unchanged when it cannot be resolved.
   */
  async getPortalAppTypeDisplayName(appTypeKey) {
    const appTypes = await this.getPortalAppTypes();
    return getAppTypeDisplayName(appTypeKey, appTypes);
  }

  /**
   * Listen for changes to the model's portal app types.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's portal app types change.
   * @returns {() => void} A function to remove the listener.
   */
  onPortalAppTypesSnapshot(callback) {
    return this.#setUpListener('portal_config/app_types', callback);
  }

  /**
   * Get the models-only subscription data for the model.
   * @returns {Promise<boolean>} Whether the model is only a model (as opposed to a company)
   */
  async getModelsOnlyFlag() {
    return await this.#readDataOnce('subscription/models_only');
  }

  /**
   * Listen for changes to the models-only subscription data for the model.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the models-only subscription data for the model changes.
   * @returns {() => void} A function to remove the listener.
   */
  onModelsOnlySnapshot(callback) {
    return this.#setUpListener('subscription/models_only', callback);
  }

  /**
   * Set the models-only subscription data for the model.
   * @param {boolean} flagValue - The value to set for the models_only flag.
   * @returns {Promise<void>} A promise that resolves when the models-only subscription data for the model has been set.
   */
  async setModelsOnlyFlag(flagValue) {
    await this.reference.child('subscription/models_only').set(flagValue);
  }

  /**
   * Get the model's tier.
   * @returns {Promise<string>} The model's tier.
   */
  async getTier() {
    return await this.#readDataOnce('tier');
  }

  /**
   * Listen for changes to the model's tier.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's tier changes.
   * @returns {() => void} A function to remove the listener.
   */
  onTierSnapshot(callback) {
    return this.#setUpListener('tier', callback);
  }

  /**
   * Set the model's tier.
   * @param {string} tier - The tier to set for the model.
   * @returns {Promise<void>} A promise that resolves when the model's tier has been set.
   */
  async setTier(tier) {
    await this.reference.child('tier').set(tier);
  }

  // ---- Model Data Path Methods ----

  /**
   * Get the entire top-level models object for the model.
   * Reads top-level keys individually, one request at a time, to avoid a single large fetch.
   * @param {object} [options] - Optional settings.
   * @param {(progress: {current: number, total: number}) => void} [options.onProgress] - Called after each top-level key is fetched.
   * @returns {Promise<object>} The full models data object.
   */
  async getModels(options = {}) {
    const topLevelKeys = await this.getTopLevelModelKeys();

    // Report immediate completion when the model has no top-level keys to fetch.
    if (!topLevelKeys.length) {
      if (typeof options?.onProgress === 'function') options.onProgress({ current: 1, total: 1 });
      return {};
    }

    let keyCounter = 0;
    const model = {};
    for (const modelKey of topLevelKeys) {
      const value = await this.#readDataOnce(`models/${modelKey}`);
      model[modelKey] = value;
      keyCounter++;
      if (typeof options?.onProgress === 'function') {
        options.onProgress({ current: keyCounter, total: topLevelKeys.length });
      }
    }

    return model;
  }

  /**
   * Gets the model's full workflow config.
   * @returns {Promise<object>} The workflow config object.
   */
  async getWorkflows() {
    const topLevelKeys = await this.getKeysAtPath('portal_config');
    if (!topLevelKeys.length) return {};

    const workflowConfig = {};
    for (const key of topLevelKeys) {
      workflowConfig[key] = await this.#readDataOnce(`portal_config/${key}`);
    }

    return workflowConfig;
  }

  /**
   * Gets the model space data.
   * @param {object} [options] - Optional settings.
   * @param {boolean} [options.includeWorkflows] - Includes the workflow config under its `portal_config` key.
   * @param {(progress: {current: number, total: number}) => void} [options.onProgress] - Called as the model space data is read.
   * @returns {Promise<ModelSpaceData>} The model space data.
   */
  async getModelSpaceData(options = {}) {
    const progressStages = { modelsDownload: { current: 0, total: 1 } };
    if (options.includeWorkflows) progressStages.workflowsDownload = { current: 0, total: 1 };
    const updateProgress = KatapultModel.trackProgress(progressStages, options?.onProgress);

    const models = await this.getModels({
      onProgress: ({ current, total }) => updateProgress('modelsDownload', current, total)
    });
    if (!options.includeWorkflows) return { models };

    const workflows = await this.getWorkflows();
    updateProgress('workflowsDownload', 1);
    return { models, portal_config: workflows };
  }

  /**
   * Checks whether the current user or their company has early access to workflow management.
   * @returns {Promise<boolean>} Whether the flag is enabled.
   */
  async #isWorkflowManagementEarlyAccessEnabled() {
    const { user_group: activeCompanyId, uid: currentUserId } = (await getAuthData(this.firebase)) ?? {};
    const authContext = { companyId: activeCompanyId, userId: currentUserId };
    return await isFlagEnabled('workflow_management_early_access', this.database, authContext);
  }

  /**
   * Gets the top-level portal config keys that a workflow config write is allowed to replace.
   * @returns {Promise<string[]>} The writable top-level portal config keys.
   */
  async #getWritablePortalConfigKeys() {
    const keys = await this.getKeysAtPath('portal_config');
    return keys.filter((key) => !KatapultModel.PORTAL_CONFIG_KEYS_TO_IGNORE.includes(key));
  }

  /**
   * Shallow-fetches the keys at a path under the model root.
   * @param {string} [path] - Path relative to `this.modelPath` (e.g. 'models/attributes', 'portal_config'). Empty fetches the model root.
   * @returns {Promise<string[]>} The keys at the path.
   */
  async getKeysAtPath(path = '') {
    const projectId = globalThis.config.appName ?? globalThis.config.projectId;
    const projectConfig = FirebaseConfig(projectId);
    const databaseURL = projectConfig?.databaseURL;
    const fullPath = path ? `${this.modelPath}/${path}` : this.modelPath;
    const shallowResult = await ShallowGet(fullPath, databaseURL);
    return Object.keys(shallowResult ?? {});
  }

  /**
   * Fetches data from the specified paths in the models data.
   * @param {string[]} paths
   * @returns {Promise<Record<string, any>>} - A promise that resolves to an object containing the data at the specified paths.
   * @throws {Error} If no valid paths are provided.
   */
  async getModelsDataAtPaths(paths) {
    const cleanedPaths = paths.map((path) => {
      const trimmedPath = path?.trim?.();
      // Reduce duplicate slashes to a single slash
      return trimmedPath?.replace(/\/+/g, '/');
    });
    const validPaths = cleanedPaths.filter((path) => path && path != '/');
    const deduplicatedPaths = [...new Set(validPaths)];

    if (deduplicatedPaths.length === 0) {
      throw new Error(`No valid paths provided`);
    }

    const result = {};
    for (const path of deduplicatedPaths) {
      const data = await this.#readDataOnce(`models/${path}`);
      result[path] = data;
    }
    return result;
  }

  /**
   * Gets the top-level keys of the model's data object using a shallow fetch.
   * @returns {Promise<string[]>} The top-level model keys.
   */
  async getTopLevelModelKeys() {
    return await this.getKeysAtPath('models');
  }

  /**
   * Replaces the model's data with the provided JSON string, writing each top-level key individually.
   * Keys present in the database but absent from the new data are explicitly deleted.
   * @param {string} modelDataString - A JSON string representing the full model data object.
   * @param {object} [options] - Optional settings.
   * @param {(progress: {current: number, total: number}) => void} [options.onProgress] - Called after each top-level key is written.
   * @returns {Promise<void>}
   * @throws {Error} If the model data string is not valid JSON or does not have the expected structure, or if any batch update fails.
   */
  async setModels(modelDataString, options = {}) {
    let parsedModelJSON;
    try {
      parsedModelJSON = JSON.parse(modelDataString);
    } catch (error) {
      console.error('Failed to parse model JSON data', error);
      throw new Error('Invalid model data: unable to parse model data');
    }

    if (!parsedModelJSON || Array.isArray(parsedModelJSON) || typeof parsedModelJSON !== 'object') {
      throw new Error('Invalid model data: expected an object of top-level model keys');
    }

    const modelDataEntries = Object.entries(parsedModelJSON);
    const existingModelKeys = await this.getTopLevelModelKeys();
    for (const existingKey of existingModelKeys) {
      const keyExistsInNewData = modelDataEntries.some(([newKey]) => newKey === existingKey);
      if (!keyExistsInNewData) {
        modelDataEntries.push([existingKey, null]);
      }
    }

    // Report immediate completion when there are no keys to write or delete.
    if (!modelDataEntries.length) {
      if (typeof options?.onProgress === 'function') options.onProgress({ current: 1, total: 1 });
      return;
    }

    let keyCounter = 0;
    for (const [modelKey, data] of modelDataEntries) {
      await LargeSet(this.reference.child(`models/${modelKey}`), data);
      keyCounter++;
      if (typeof options?.onProgress === 'function') {
        options.onProgress({ current: keyCounter, total: modelDataEntries.length });
      }
    }
  }

  /**
   * Writes the model's workflow config, updating or deleting every key except those in {@link KatapultModel.PORTAL_CONFIG_KEYS_TO_IGNORE}.
   * @param {object} workflowConfig An empty object clears the config.
   * @returns {Promise<void>}
   * @throws {Error} If no workflow config is provided, or if it is not an object of top-level workflow config keys.
   */
  async setWorkflows(workflowConfig) {
    if (!workflowConfig) throw new Error('Workflow config is required');
    if (typeof workflowConfig !== 'object' || Array.isArray(workflowConfig)) {
      throw new Error('Invalid model data: workflow config must be an object of top-level workflow config keys');
    }

    const workflowConfigEntries = Object.entries(workflowConfig).filter(
      ([key]) => !KatapultModel.PORTAL_CONFIG_KEYS_TO_IGNORE.includes(key)
    );

    // Get existing keys and null out any missing from the new config.
    const existingWorkflowConfigKeys = await this.#getWritablePortalConfigKeys();
    for (const existingKey of existingWorkflowConfigKeys) {
      const keyExistsInNewData = workflowConfigEntries.some(([newKey]) => newKey === existingKey);
      if (!keyExistsInNewData) {
        workflowConfigEntries.push([existingKey, null]);
      }
    }

    // Write all new config keys.
    for (const [key, data] of workflowConfigEntries) {
      await LargeSet(this.reference.child(`portal_config/${key}`), data);
    }
  }

  /**
   * Parses model space data from JSON, wrapping legacy data that holds the model keys at its root.
   * @param {string} modelSpaceDataString - A JSON string of model space data.
   * @returns {ModelSpaceData} The parsed model space data.
   * @throws {Error} If the JSON cannot be parsed, or if the model data or workflow config is not an object.
   */
  static parseModelSpaceData(modelSpaceDataString) {
    let parsedData;
    try {
      parsedData = JSON.parse(modelSpaceDataString);
    } catch (error) {
      console.error('Failed to parse model space JSON data', error);
      throw new Error('Invalid model data: unable to parse model data');
    }

    const isPlainObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);
    if (!isPlainObject(parsedData)) throw new Error('Invalid model data: expected an object of model space data');

    // Old exports contained only models, with no model space wrapper.
    const isModelSpaceShaped = isPlainObject(parsedData.models) || isPlainObject(parsedData.portal_config);
    if (!isModelSpaceShaped) parsedData = { models: parsedData };

    if (!isPlainObject(parsedData.models)) throw new Error('Invalid model data: expected an object of top-level model keys');

    // Old exports could store the workflow config as "workflows" within models.
    const { workflows: legacyExportWorkflows, ...models } = parsedData.models;

    const hasConflictingWorkflowConfigs = parsedData.portal_config != null && legacyExportWorkflows != null;
    if (hasConflictingWorkflowConfigs)
      throw new Error('Invalid model data: workflow config found both under portal_config and within the model data');

    const workflowConfig = parsedData.portal_config ?? legacyExportWorkflows;
    if (workflowConfig != null && !isPlainObject(workflowConfig))
      throw new Error('Invalid model data: workflow config must be an object of top-level workflow config keys');

    return { models, ...(workflowConfig != null && { portal_config: workflowConfig }) };
  }

  /**
   * Writes model space data over the model, replacing its model data.
   * @param {ModelSpaceData} modelSpaceData
   * @param {object} [options] - Optional settings.
   * @param {boolean} [options.includeWorkflows] - Optionally sets the workflows config when the model space data specifies it.
   * @param {(progress: {current: number, total: number}) => void} [options.onProgress] - Called as the model space data is written.
   * @returns {Promise<void>}
   */
  async setModelSpaceData(modelSpaceData, options = {}) {
    const progressStages = { modelsUpload: { current: 0, total: 1 } };
    if (options.includeWorkflows) progressStages.workflowsUpload = { current: 0, total: 1 };
    const updateProgress = KatapultModel.trackProgress(progressStages, options?.onProgress);

    await this.setModels(JSON.stringify(modelSpaceData.models), {
      onProgress: ({ current, total }) => updateProgress('modelsUpload', current, total)
    });
    if (!options.includeWorkflows) return;

    if (modelSpaceData.portal_config != null) await this.setWorkflows(modelSpaceData.portal_config);
    updateProgress('workflowsUpload', 1);
  }

  /**
   * Creates a model snapshot by reading the current model data and uploading it to object storage.
   * Also writes snapshot metadata to the database.
   * @param {string} snapshotName - The user-facing name for the snapshot.
   * @param {string} modelOwner - The company that owns the model (used for storage path scoping).
   * @param {string} createdByUid - The uid of the user creating the snapshot.
   * @param {object} [options] - Optional settings.
   * @param {(progress: {current: number, total: number}) => void} [options.onProgress] - Called as model data is read and the snapshot is uploaded.
   * @returns {Promise<void>}
   * @throws {Error} If no snapshot name model owner, or user id is provided. Or if the snapshot limit has been reached.
   */
  async createSnapshot(snapshotName, modelOwner, createdByUid, options = {}) {
    if (!snapshotName) throw new Error('Snapshot name is required');
    if (!modelOwner) throw new Error('Model owner is required');
    if (!createdByUid) throw new Error('Creator uid is required');

    const existingSnapshots = await this.listSnapshots();
    if (existingSnapshots.length >= 20) {
      throw new Error('Snapshot limit reached: please delete existing snapshots before creating new ones');
    }

    const isWorkflowManagementEarlyAccessEnabled = await this.#isWorkflowManagementEarlyAccessEnabled();

    // Track progress for different stages.
    const progressStages = {
      modelSpaceDownload: { current: 0, total: 1 },
      snapshotUpload: { current: 0, total: 1 }
    };
    const updateProgress = KatapultModel.trackProgress(progressStages, options?.onProgress);

    // Download the model space data.
    const modelSpaceData = await this.getModelSpaceData({
      includeWorkflows: isWorkflowManagementEarlyAccessEnabled,
      onProgress: ({ current, total }) => {
        updateProgress('modelSpaceDownload', current, total);
      }
    });

    // Upload the serialized snapshot
    const storage = getClientOrAdminStorage(this.firebase);
    const snapshotId = globalThis.crypto.randomUUID();
    const storagePath = `${SNAPSHOT_STORAGE_ROOT}/${modelOwner}/${this.id}/${SNAPSHOT_FILE_ROOT}/${snapshotId}.json`;

    const serialized = JSON.stringify(modelSpaceData);
    const payload = new Blob([serialized], { type: 'application/json' });

    await storage.ref(storagePath).put(payload);
    updateProgress('snapshotUpload', 1);

    /** @type SnapshotMetadata */
    const metadata = {
      id: snapshotId,
      name: snapshotName,
      model_id: this.id,
      storage_path: storagePath,
      created_at: Date.now(),
      created_by_uid: createdByUid,
      size_bytes: serialized.length
    };

    await this.database.ref(this.getSnapshotMetadataPath(this.id, snapshotId)).set(metadata);
  }

  /**
   * Lists all snapshot metadata entries for this model, sorted by creation date descending.
   * @returns {Promise<SnapshotMetadata[]>} The snapshot metadata entries, newest first.
   */
  async listSnapshots() {
    const allSnapshotsMetadataPath = this.getSnapshotMetadataPath(this.id);
    /** @type {Record<string, SnapshotMetadata>} */
    const snapshotsData = (await this.database.ref(allSnapshotsMetadataPath).once('value')).val();
    return Object.values(snapshotsData ?? {}).sort((a, b) => (b?.created_at ?? 0) - (a?.created_at ?? 0));
  }

  /**
   * Downloads the raw JSON string for a snapshot from object storage.
   * @param {string} storagePath - The object storage path of the snapshot file.
   * @returns {Promise<string>} The raw JSON string of the snapshot data.
   * @throws {Error} If the storage path is missing or the download request fails.
   */
  async readSnapshotData(storagePath) {
    if (!storagePath) throw new Error('Missing snapshot storage path');
    const storage = getClientOrAdminStorage(this.firebase);
    const url = await storage.ref(storagePath).getDownloadURL();
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch snapshot data (${response.status})`);
    return await response.text();
  }

  /**
   * Restores model data from a snapshot by downloading the snapshot JSON and writing it back to the database.
   * @param {SnapshotMetadata} snapshotMetadata - The metadata record for the snapshot to restore.
   * @param {object} [options] - Optional settings.
   * @param {(progress: {current: number, total: number}) => void} [options.onProgress] - Called as each top-level key is written during restore.
   * @returns {Promise<void>}
   * @throws {Error} If the snapshot metadata is missing.
   */
  async restoreFromSnapshot(snapshotMetadata, options = {}) {
    if (!snapshotMetadata) throw new Error('Missing snapshot reference');

    const isWorkflowManagementEarlyAccessEnabled = await this.#isWorkflowManagementEarlyAccessEnabled();

    // Track progress for different stages.
    const progressStages = {
      modelSpaceUpload: { current: 0, total: 1 }
    };
    const updateProgress = KatapultModel.trackProgress(progressStages, options?.onProgress);

    // Download and parse the snapshot before writing anything.
    const snapshotData = await this.readSnapshotData(snapshotMetadata.storage_path);
    const modelSpaceData = KatapultModel.parseModelSpaceData(snapshotData);

    await this.setModelSpaceData(modelSpaceData, {
      includeWorkflows: isWorkflowManagementEarlyAccessEnabled,
      onProgress: ({ current, total }) => {
        updateProgress('modelSpaceUpload', current, total);
      }
    });
  }

  /**
   * Renames a snapshot by updating its name in the database metadata.
   * @param {SnapshotMetadata} snapshotMetadata - The metadata record for the snapshot to rename.
   * @param {string} name - The new name for the snapshot.
   * @returns {Promise<void>}
   * @throws {Error} If the snapshot metadata is missing.
   */
  async renameSnapshot(snapshotMetadata, name) {
    if (!snapshotMetadata) throw new Error('Missing snapshot reference');
    await this.database.ref(`${this.getSnapshotMetadataPath(this.id, snapshotMetadata.id)}/name`).set(name);
  }

  /**
   * Deletes a snapshot's object storage file and its database metadata record.
   * @param {SnapshotMetadata} snapshotMetadata - The metadata record for the snapshot to delete.
   * @returns {Promise<void>}
   * @throws {Error} If the snapshot metadata is missing.
   */
  async deleteSnapshot(snapshotMetadata) {
    if (!snapshotMetadata) throw new Error('Missing snapshot reference');
    const storage = getClientOrAdminStorage(this.firebase);
    if (snapshotMetadata?.storage_path) {
      await storage.ref(snapshotMetadata.storage_path).delete();
    }
    await this.database.ref(this.getSnapshotMetadataPath(this.id, snapshotMetadata.id)).remove();
  }

  /**
   * Returns the database path to a model's snapshot metadata, optionally scoped to a single snapshot.
   * @param {string} modelId - The id of the model.
   * @param {string} [snapshotId] - The id of the snapshot. If omitted, returns the path to all snapshots for the model.
   * @returns {string} The database path.
   */
  getSnapshotMetadataPath(modelId, snapshotId) {
    const suffix = snapshotId ? `/${snapshotId}` : '';
    return `photoheight/company_space/${modelId}/snapshot_metadata${suffix}`;
  }

  /**
   * Get the model's action tracking data.
   * @returns {Promise<Object>} The model's action tracking data.
   */
  async getActionTracking() {
    return await this.#readDataOnce('models/action_tracking');
  }

  /**
   * Get the model's action tracking model lookup map.
   * @returns {Promise<Object|null>} The tracking model lookup map.
   */
  async getActionTrackingModelLookup() {
    return await this.#readDataOnce('models/action_tracking/_tracking_model_lookup');
  }

  /**
   * Get the model's default action tracking model.
   * @returns {Promise<string>} The default action tracking model value.
   */
  async getDefaultActionTrackingModel() {
    return await this.#readDataOnce('models/action_tracking/_default');
  }

  /**
   * Get a specific action tracking model by key.
   * @param {string} key - The action tracking model key.
   * @returns {Promise<Object|null>} The action tracking model data.
   */
  async getActionTrackingModel(key) {
    return await this.#readDataOnce(`models/action_tracking/${key}`);
  }

  /**
   * Listen for changes to the model's action tracking data.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's action tracking data changes.
   * @returns {() => void} A function to remove the listener.
   */
  onActionTrackingSnapshot(callback) {
    return this.#setUpListener('models/action_tracking', callback);
  }

  /**
   * Set the model's action tracking data.
   * @param {Object} actionTracking - The action tracking data to set for the model.
   * @returns {Promise<void>} A promise that resolves when the action tracking data has been set.
   */
  async setActionTracking(actionTracking) {
    throw new Error('setActionTracking not yet implemented');
  }

  /**
   * Get the model's alternate designs.
   * @returns {Promise<Object>} The model's alternate designs.
   */
  async getAlternateDesigns() {
    return await this.#readDataOnce('models/alternate_designs');
  }

  /**
   * Listen for changes to the model's alternate designs.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's alternate designs change.
   * @returns {() => void} A function to remove the listener.
   */
  onAlternateDesignsSnapshot(callback) {
    return this.#setUpListener('models/alternate_designs', callback);
  }

  /**
   * Set the model's alternate designs.
   * @param {Object} alternateDesigns - The alternate designs to set for the model.
   * @returns {Promise<void>} A promise that resolves when the alternate designs have been set.
   */
  async setAlternateDesigns(alternateDesigns) {
    throw new Error('setAlternateDesigns not yet implemented');
  }

  /**
   * Get the model's api payload.
   * @returns {Promise<Object>} The model's api payload.
   */
  async getApiPayload() {
    return await this.#readDataOnce('models/api_payload');
  }

  /**
   * Listen for changes to the model's api payload.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's api payload changes.
   * @returns {() => void} A function to remove the listener.
   */
  onApiPayloadSnapshot(callback) {
    return this.#setUpListener('models/api_payload', callback);
  }

  /**
   * Set the model's api payload.
   * @param {Object} apiPayload - The api payload to set for the model.
   * @returns {Promise<void>} A promise that resolves when the api payload has been set.
   */
  async setApiPayload(apiPayload) {
    throw new Error('setApiPayload not yet implemented');
  }

  /**
   * Get the model's application companies.
   * @returns {Promise<Object>} The model's application companies.
   */
  async getApplicationCompanies() {
    return await this.#readDataOnce('models/application_companies');
  }

  /**
   * Listen for changes to the model's application companies.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's application companies change.
   * @returns {() => void} A function to remove the listener.
   */
  onApplicationCompaniesSnapshot(callback) {
    return this.#setUpListener('models/application_companies', callback);
  }

  /**
   * Set the model's application companies.
   * @param {Object} applicationCompanies - The application companies to set for the model.
   * @returns {Promise<void>} A promise that resolves when the application companies have been set.
   */
  async setApplicationCompanies(applicationCompanies) {
    throw new Error('setApplicationCompanies not yet implemented');
  }

  /**
   * Get the model's attribute groups.
   * @returns {Promise<Object>} The model's attribute groups.
   */
  async getAttributeGroups() {
    return await this.#readDataOnce('models/attribute_groups');
  }

  /**
   * Listen for changes to the model's attribute groups.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's attribute groups change.
   * @returns {() => void} A function to remove the listener.
   */
  onAttributeGroupsSnapshot(callback) {
    return this.#setUpListener('models/attribute_groups', callback);
  }

  /**
   * Set the model's attribute groups.
   * @param {Object} attributeGroups - The attribute groups to set for the model.
   * @returns {Promise<void>} A promise that resolves when the attribute groups have been set.
   */
  async setAttributeGroups(attributeGroups) {
    return await this.reference.child('models/attribute_groups').set(attributeGroups);
  }

  /**
   * Get the model's attribute sets.
   * @returns {Promise<Object>} The model's attribute sets.
   */
  async getAttributeSets() {
    return await this.#readDataOnce('models/attribute_sets');
  }

  /**
   * Listen for changes to the model's attribute sets.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's attribute sets change.
   * @returns {() => void} A function to remove the listener.
   */
  onAttributeSetsSnapshot(callback) {
    return this.#setUpListener('models/attribute_sets', callback);
  }

  /**
   * Get a specific attribute set by key.
   * @param {string} key - The attribute set key.
   * @returns {Promise<Object|null>} The attribute set.
   */
  async getAttributeSet(key) {
    return await this.#readDataOnce(`models/attribute_sets/${key}`);
  }

  /**
   * Set the model's attribute sets.
   * @param {Object} attributeSets - The attribute sets to set for the model.
   * @returns {Promise<void>} A promise that resolves when the attribute sets have been set.
   */
  async setAttributeSets(attributeSets) {
    throw new Error('setAttributeSets not yet implemented');
  }

  /**
   * Get the model's attributes.
   * @returns {Promise<Object<string, ModelAttribute>>} The model's attributes, keyed by attribute key.
   */
  async getAttributes() {
    return await this.#readDataOnce('models/attributes');
  }

  /**
   * Listen for changes to the model's attributes.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's attributes change.
   * @returns {() => void} A function to remove the listener.
   */
  onAttributesSnapshot(callback) {
    return this.#setUpListener('models/attributes', callback);
  }

  /**
   * Get a specific attribute by key.
   * @param {string} attribute - The attribute key.
   * @returns {Promise<Object|null>} The attribute.
   */
  async getAttribute(attribute) {
    return await this.#readDataOnce(`models/attributes/${attribute}`);
  }

  /**
   * Get the GUI element type for an attribute.
   * @param {string} attribute
   * @returns {Promise<string|null>} The gui_element type (e.g. "text", "dropdown", etc.)
   */
  async getAttributeGuiElement(attribute) {
    return await this.#readDataOnce(`models/attributes/${attribute}/gui_element`);
  }

  /**
   * Listen for changes to a specific attribute by key.
   * @param {string} attribute - The attribute key.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the attribute changes.
   * @returns {() => void} A function to remove the listener.
   */
  onAttributeSnapshot(attribute, callback) {
    return this.#setUpListener(`models/attributes/${attribute}`, callback);
  }

  /**
   * Set a specific attribute by key.
   * @param {string} attribute - The attribute key.
   * @param {Object} value - The value to set for the attribute.
   * @returns {Promise<void>} A promise that resolves when the attribute has been set.
   */
  async setAttribute(attribute, value) {
    return await this.reference.child(`models/attributes/${attribute}`).set(value);
  }

  /**
   * Set the model's attributes.
   * @param {Object} attributes - The attributes to set for the model.
   * @returns {Promise<void>} A promise that resolves when the attributes have been set.
   */
  async setAttributes(attributes) {
    throw new Error('setAttributes not yet implemented');
  }

  /**
   * Get the model's button groups.
   * @returns {Promise<Object>} The model's button groups.
   */
  async getButtonGroups() {
    return await this.#readDataOnce('models/button_groups');
  }

  /**
   * Listen for changes to the model's button groups.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's button groups change.
   * @returns {() => void} A function to remove the listener.
   */
  onButtonGroupsSnapshot(callback) {
    return this.#setUpListener('models/button_groups', callback);
  }

  /**
   * Set the model's button groups.
   * @param {Object} buttonGroups - The button groups to set for the model.
   * @returns {Promise<void>} A promise that resolves when the button groups have been set.
   */
  async setButtonGroups(buttonGroups) {
    throw new Error('setButtonGroups not yet implemented');
  }

  /**
   * Get the model's collection order logic.
   * @returns {Promise<Object>} The model's collection order logic.
   */
  async getCollectionOrderLogic() {
    return await this.#readDataOnce('models/collection_order_logic');
  }

  /**
   * Listen for changes to the model's collection order logic.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's collection order logic changes.
   * @returns {() => void} A function to remove the listener.
   */
  onCollectionOrderLogicSnapshot(callback) {
    return this.#setUpListener('models/collection_order_logic', callback);
  }

  /**
   * Set the model's collection order logic.
   * @param {Object} collectionOrderLogic - The collection order logic to set for the model.
   * @returns {Promise<void>} A promise that resolves when the collection order logic has been set.
   */
  async setCollectionOrderLogic(collectionOrderLogic) {
    throw new Error('setCollectionOrderLogic not yet implemented');
  }

  /**
   * Get the model's compatible unit entry.
   * @returns {Promise<Object>} The model's compatible unit entry.
   */
  async getCompatibleUnitEntry() {
    return await this.#readDataOnce('models/compatible_unit_entry');
  }

  /**
   * Listen for changes to the model's compatible unit entry.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's compatible unit entry changes.
   * @returns {() => void} A function to remove the listener.
   */
  onCompatibleUnitEntrySnapshot(callback) {
    return this.#setUpListener('models/compatible_unit_entry', callback);
  }

  /**
   * Set the model's compatible unit entry.
   * @param {Object} compatibleUnitEntry - The compatible unit entry to set for the model.
   * @returns {Promise<void>} A promise that resolves when the compatible unit entry has been set.
   */
  async setCompatibleUnitEntry(compatibleUnitEntry) {
    throw new Error('setCompatibleUnitEntry not yet implemented');
  }

  /**
   * Get the model's compatible unit models.
   * @returns {Promise<Object>} The model's compatible unit models.
   */
  async getCompatibleUnitModels() {
    return await this.#readDataOnce('models/compatible_unit_models');
  }

  /**
   * Get the model's compatible unit model automation groupings.
   * @returns {Promise<Object|null>} The automation groupings.
   */
  async getCompatibleUnitModelsAutomationGroupings() {
    return await this.#readDataOnce('models/compatible_unit_models/automation_groupings');
  }

  /**
   * Listen for changes to the model's compatible unit models.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's compatible unit models change.
   * @returns {() => void} A function to remove the listener.
   */
  onCompatibleUnitModelsSnapshot(callback) {
    return this.#setUpListener('models/compatible_unit_models', callback);
  }

  /**
   * Set the model's compatible unit models.
   * @param {Object} compatibleUnitModels - The compatible unit models to set for the model.
   * @returns {Promise<void>} A promise that resolves when the compatible unit models have been set.
   */
  async setCompatibleUnitModels(compatibleUnitModels) {
    throw new Error('setCompatibleUnitModels not yet implemented');
  }

  /**
   * Get the model's compatible units.
   * @returns {Promise<Object>} The model's compatible units.
   */
  async getCompatibleUnits() {
    return await this.#readDataOnce('models/compatible_unit_models/cus');
  }

  /**
   * Listen for changes to the model's compatible units.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's compatible units change.
   * @returns {() => void} A function to remove the listener.
   */
  onCompatibleUnitsSnapshot(callback) {
    return this.#setUpListener('models/compatible_unit_models/cus', callback);
  }

  /**
   * Set the model's compatible units.
   * @param {Object} compatibleUnits - The compatible units to set for the model.
   * @returns {Promise<void>} A promise that resolves when the compatible units have been set.
   */
  async setCompatibleUnits(compatibleUnits) {
    throw new Error('setCompatibleUnits not yet implemented');
  }

  /**
   * Get the model's config.
   * @returns {Promise<Object>} The model's config.
   */
  async getConfig() {
    return await this.#readDataOnce('models/config');
  }

  /**
   * Listen for changes to the model's config.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's config changes.
   * @returns {() => void} A function to remove the listener.
   */
  onConfigSnapshot(callback) {
    return this.#setUpListener('models/config', callback);
  }

  /**
   * Get the model's config defaults.
   * @returns {Promise<Object|null>} The model's config defaults.
   */
  async getModelDefaults() {
    return await this.#readDataOnce('models/config/defaults');
  }

  /**
   * Get the model's default map styles.
   * @returns {Promise<Object|null>} The model's default map styles.
   */
  async getDefaultMapStyles() {
    return await this.#readDataOnce('models/config/map/default_map_styles');
  }

  /**
   * Get the model's default saved view key.
   * @returns {Promise<string|null>} The key of the model's default saved view.
   */
  async getDefaultSavedView() {
    return await this.#readDataOnce('models/config/map/default_saved_view');
  }

  /**
   * Get the model's time zone.
   * @returns {Promise<string|null>} The model's time zone.
   */
  async getTimeZone() {
    return await this.#readDataOnce('models/config/time_zone');
  }

  /**
   * Listen for changes to the model's time zone.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's time zone changes.
   * @returns {() => void} A function to remove the listener.
   */
  onTimeZoneSnapshot(callback) {
    return this.#setUpListener('models/config/time_zone', callback);
  }

  /**
   * Set the model's time zone.
   * @param {string} timeZone - The time zone to set for the model.
   * @returns {Promise<void>} A promise that resolves when the time zone has been set.
   */
  async setTimeZone(timeZone) {
    throw new Error('setTimeZone not yet implemented');
  }

  /**
   * Get the model's photo export classification sort order.
   * @returns {Promise<Object|null>} The model's photo export classification sort order.
   */
  async getPhotoExportClassificationOrder() {
    return await this.#readDataOnce('models/config/photo_export_classification_order');
  }

  /**
   * Listen for changes to the model's photo export classification sort order.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the sort order changes.
   * @returns {() => void} A function to remove the listener.
   */
  onPhotoExportClassificationOrderSnapshot(callback) {
    return this.#setUpListener('models/config/photo_export_classification_order', callback);
  }

  /**
   * Set the model's photo export classification sort order.
   * @param {Object} order - The classification sort order to set for the model.
   * @returns {Promise<void>} A promise that resolves when the sort order has been set.
   */
  async setPhotoExportClassificationOrder(order) {
    throw new Error('setPhotoExportClassificationOrder not yet implemented');
  }

  /**
   * Get the model's config map settings.
   * @returns {Promise<Object|null>} The model's config map settings.
   */
  async getConfigMap() {
    return await this.#readDataOnce('models/config/map');
  }

  /**
   * Listen for changes to the model's config map settings.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's config map settings change.
   * @returns {() => void} A function to remove the listener.
   */
  onConfigMapSnapshot(callback) {
    return this.#setUpListener('models/config/map', callback);
  }

  /**
   * Set the model's config.
   * @param {Object} config - The config to set for the model.
   * @returns {Promise<void>} A promise that resolves when the config has been set.
   */
  async setConfig(config) {
    throw new Error('setConfig not yet implemented');
  }

  /**
   * Get the model's construction specs.
   * @returns {Promise<Object>} The model's construction specs.
   */
  async getConstructionSpecs() {
    return await this.#readDataOnce('models/construction_specs');
  }

  /**
   * Listen for changes to the model's construction specs.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's construction specs change.
   * @returns {() => void} A function to remove the listener.
   */
  onConstructionSpecsSnapshot(callback) {
    return this.#setUpListener('models/construction_specs', callback);
  }

  /**
   * Get a specific construction spec by key.
   * @param {string} key - The construction spec key.
   * @returns {Promise<Object|null>} The construction spec.
   */
  async getConstructionSpec(key) {
    return await this.#readDataOnce(`models/construction_specs/${key}`);
  }

  /**
   * Set the model's construction specs.
   * @param {Object} constructionSpecs - The construction specs to set for the model.
   * @returns {Promise<void>} A promise that resolves when the construction specs have been set.
   */
  async setConstructionSpecs(constructionSpecs) {
    throw new Error('setConstructionSpecs not yet implemented');
  }

  /**
   * Get the model's custom icon collections.
   * @returns {Promise<Object>} The model's custom icon collections.
   */
  async getCustomIconCollections() {
    return await this.#readDataOnce('models/custom_icon_collections');
  }

  /**
   * Listen for changes to the model's custom icon collections.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's custom icon collections change.
   * @returns {() => void} A function to remove the listener.
   */
  onCustomIconCollectionsSnapshot(callback) {
    return this.#setUpListener('models/custom_icon_collections', callback);
  }

  /**
   * Set the model's custom icon collections.
   * @param {Object} customIconCollections - The custom icon collections to set for the model.
   * @returns {Promise<void>} A promise that resolves when the custom icon collections have been set.
   */
  async setCustomIconCollections(customIconCollections) {
    throw new Error('setCustomIconCollections not yet implemented');
  }

  /**
   * Get the model's custom icons.
   * @returns {Promise<Object>} The model's custom icons.
   */
  async getCustomIcons() {
    return await this.#readDataOnce('models/custom_icons');
  }

  /**
   * Listen for changes to the model's custom icons.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's custom icons change.
   * @returns {() => void} A function to remove the listener.
   */
  onCustomIconsSnapshot(callback) {
    return this.#setUpListener('models/custom_icons', callback);
  }

  /**
   * Set the model's custom icons.
   * @param {Object} customIcons - The custom icons to set for the model.
   * @returns {Promise<void>} A promise that resolves when the custom icons have been set.
   */
  async setCustomIcons(customIcons) {
    throw new Error('setCustomIcons not yet implemented');
  }

  /**
   * Get the model's custom line styles.
   * @returns {Promise<Object>} The model's custom line styles.
   */
  async getCustomLineStyles() {
    return await this.#readDataOnce('models/custom_line_styles');
  }

  /**
   * Listen for changes to the model's custom line styles.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's custom line styles change.
   * @returns {() => void} A function to remove the listener.
   */
  onCustomLineStylesSnapshot(callback) {
    return this.#setUpListener('models/custom_line_styles', callback);
  }

  /**
   * Set the model's custom line styles.
   * @param {Object} customLineStyles - The custom line styles to set for the model.
   * @returns {Promise<void>} A promise that resolves when the custom line styles have been set.
   */
  async setCustomLineStyles(customLineStyles) {
    throw new Error('setCustomLineStyles not yet implemented');
  }

  /**
   * Get the model's drawing disallowed attachments.
   * @returns {Promise<Object>} The model's drawing disallowed attachments.
   */
  async getDrawingDisallowedAttachments() {
    return await this.#readDataOnce('models/drawing_disallowed_attachments');
  }

  /**
   * Listen for changes to the model's drawing disallowed attachments.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's drawing disallowed attachments change.
   * @returns {() => void} A function to remove the listener.
   */
  onDrawingDisallowedAttachmentsSnapshot(callback) {
    return this.#setUpListener('models/drawing_disallowed_attachments', callback);
  }

  /**
   * Set the model's drawing disallowed attachments.
   * @param {Object} drawingDisallowedAttachments - The drawing disallowed attachments to set for the model.
   * @returns {Promise<void>} A promise that resolves when the drawing disallowed attachments have been set.
   */
  async setDrawingDisallowedAttachments(drawingDisallowedAttachments) {
    throw new Error('setDrawingDisallowedAttachments not yet implemented');
  }

  /**
   * Get the model's export models.
   * @returns {Promise<Object>} The model's export models.
   */
  async getExportModels() {
    return await this.#readDataOnce('models/export_models');
  }

  /**
   * Listen for changes to the model's export models.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's export models change.
   * @returns {() => void} A function to remove the listener.
   */
  onExportModelsSnapshot(callback) {
    return this.#setUpListener('models/export_models', callback);
  }

  /**
   * Set the model's export models.
   * @param {Object} exportModels - The export models to set for the model.
   * @returns {Promise<void>} A promise that resolves when the export models have been set.
   */
  async setExportModels(exportModels) {
    throw new Error('setExportModels not yet implemented');
  }

  /**
   * Get the model's Google Sheets export config.
   * @returns {Promise<Object|null>} The Google Sheets export config.
   */
  async getGoogleSheetsExportModel() {
    return await this.#readDataOnce('models/export_models/google_sheets');
  }

  /**
   * Get a specific photo form template's model config.
   * @param {string} templateKey - The key of the photo form template.
   * @returns {Promise<Object|null>} The photo form template model config.
   */
  async getPhotoFormTemplateModel(templateKey) {
    return await this.#readDataOnce(`models/export_models/photo_form_templates/${templateKey}/model`);
  }

  /**
   * Get the model's files.
   * @returns {Promise<Object>} The model's files.
   */
  async getFiles() {
    return await this.#readDataOnce('models/files');
  }

  /**
   * Listen for changes to the model's files.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's files change.
   * @returns {() => void} A function to remove the listener.
   */
  onFilesSnapshot(callback) {
    return this.#setUpListener('models/files', callback);
  }

  /**
   * Set the model's files.
   * @param {Object} files - The files to set for the model.
   * @returns {Promise<void>} A promise that resolves when the files have been set.
   */
  async setFiles(files) {
    throw new Error('setFiles not yet implemented');
  }

  /**
   * Get the model's hardware details options.
   * @returns {Promise<Object>} The model's hardware details options.
   */
  async getHwDetailsOptions() {
    return await this.#readDataOnce('models/hw_details_options');
  }

  /**
   * Listen for changes to the model's hardware details options.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's hardware details options change.
   * @returns {() => void} A function to remove the listener.
   */
  onHwDetailsOptionsSnapshot(callback) {
    return this.#setUpListener('models/hw_details_options', callback);
  }

  /**
   * Set the model's hardware details options.
   * @param {Object} hwDetailsOptions - The hardware details options to set for the model.
   * @returns {Promise<void>} A promise that resolves when the hardware details options have been set.
   */
  async setHwDetailsOptions(hwDetailsOptions) {
    throw new Error('setHwDetailsOptions not yet implemented');
  }

  /**
   * Get the model's image recognition models.
   * @returns {Promise<Object>} The model's image recognition models.
   */
  async getImageRecognitionModels() {
    return await this.#readDataOnce('models/image_recognition_models');
  }

  /**
   * Listen for changes to the model's image recognition models.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's image recognition models change.
   * @returns {() => void} A function to remove the listener.
   */
  onImageRecognitionModelsSnapshot(callback) {
    return this.#setUpListener('models/image_recognition_models', callback);
  }

  /**
   * Set the model's image recognition models.
   * @param {Object} imageRecognitionModels - The image recognition models to set for the model.
   * @returns {Promise<void>} A promise that resolves when the image recognition models have been set.
   */
  async setImageRecognitionModels(imageRecognitionModels) {
    throw new Error('setImageRecognitionModels not yet implemented');
  }

  /**
   * Get the model's import models.
   * @returns {Promise<Object>} The model's import models.
   */
  async getImportModels() {
    return await this.#readDataOnce('models/import_models');
  }

  /**
   * Listen for changes to the model's import models.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's import models change.
   * @returns {() => void} A function to remove the listener.
   */
  onImportModelsSnapshot(callback) {
    return this.#setUpListener('models/import_models', callback);
  }

  /**
   * Set the model's import models.
   * @param {Object} importModels - The import models to set for the model.
   * @returns {Promise<void>} A promise that resolves when the import models have been set.
   */
  async setImportModels(importModels) {
    throw new Error('setImportModels not yet implemented');
  }

  /**
   * Get the model's input model groups.
   * @returns {Promise<Object>} The model's input model groups.
   */
  async getInputModelGroups() {
    return await this.#readDataOnce('models/input_model_groups');
  }

  /**
   * Listen for changes to the model's input model groups.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's input model groups change.
   * @returns {() => void} A function to remove the listener.
   */
  onInputModelGroupsSnapshot(callback) {
    return this.#setUpListener('models/input_model_groups', callback);
  }

  /**
   * Set the model's input model groups.
   * @param {Object} inputModelGroups - The input model groups to set for the model.
   * @returns {Promise<void>} A promise that resolves when the input model groups have been set.
   */
  async setInputModelGroups(inputModelGroups) {
    throw new Error('setInputModelGroups not yet implemented');
  }

  /**
   * Get the model's input models.
   * @returns {Promise<Object>} The model's input models.
   */
  async getInputModels() {
    return await this.#readDataOnce('models/input_models');
  }

  /**
   * Listen for changes to the model's input models.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's input models change.
   * @returns {() => void} A function to remove the listener.
   */
  onInputModelsSnapshot(callback) {
    return this.#setUpListener('models/input_models', callback);
  }

  /**
   * Set the model's input models.
   * @param {Object} inputModels - The input models to set for the model.
   * @returns {Promise<void>} A promise that resolves when the input models have been set.
   */
  async setInputModels(inputModels) {
    throw new Error('setInputModels not yet implemented');
  }

  /**
   * Get a specific input model by key.
   * @param {string} inputModelKey - The input model key.
   * @returns {Promise<Object|null>} The input model.
   */
  async getInputModel(inputModelKey) {
    return await this.#readDataOnce(`models/input_models/${inputModelKey}`);
  }

  /**
   * Set a specific input model by key.
   * @param {string} inputModelKey - The input model key.
   * @param {Object} value - The value to set for the input model.
   * @returns {Promise<void>} A promise that resolves when the input model has been set.
   */
  async setInputModel(inputModelKey, value) {
    throw new Error('setInputModel not yet implemented');
  }

  /**
   * Get the model's job tracker.
   * @returns {Promise<Object>} The model's job tracker.
   */
  async getJobTracker() {
    return await this.#readDataOnce('models/job_tracker');
  }

  /**
   * Listen for changes to the model's job tracker.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's job tracker changes.
   * @returns {() => void} A function to remove the listener.
   */
  onJobTrackerSnapshot(callback) {
    return this.#setUpListener('models/job_tracker', callback);
  }

  /**
   * Set the model's job tracker.
   * @param {Object} jobTracker - The job tracker to set for the model.
   * @returns {Promise<void>} A promise that resolves when the job tracker has been set.
   */
  async setJobTracker(jobTracker) {
    throw new Error('setJobTracker not yet implemented');
  }

  /**
   * Get the model's job tracker workflows.
   * @returns {Promise<Object|null>} The job tracker workflows.
   */
  async getJobTrackerWorkflows() {
    return await this.#readDataOnce('models/job_tracker/workflows');
  }

  /**
   * Get a specific job tracker workflow.
   * @param {string} workflowKey - The key of the workflow.
   * @returns {Promise<Object|null>} The workflow config.
   */
  async getJobTrackerWorkflow(workflowKey) {
    return await this.#readDataOnce(`models/job_tracker/workflows/${workflowKey}`);
  }

  /**
   * Get the model's job tracker bulk update emails.
   * @returns {Promise<Array>} The bulk update emails config.
   */
  async getJobTrackerBulkUpdateEmails() {
    return await this.#readDataOnce('models/job_tracker/bulk_update_emails');
  }

  /**
   * Get the model's make ready configuration.
   * @returns {Promise<Object>} The model's make ready configuration.
   */
  async getMakeReady() {
    return await this.#readDataOnce('models/make_ready');
  }

  /**
   * Listen for changes to the model's make ready configuration.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's make ready configuration changes.
   * @returns {() => void} A function to remove the listener.
   */
  onMakeReadySnapshot(callback) {
    return this.#setUpListener('models/make_ready', callback);
  }

  /**
   * Set the model's make ready configuration.
   * @param {Object} makeReady - The make ready configuration to set for the model.
   * @returns {Promise<void>} A promise that resolves when the make ready configuration has been set.
   */
  async setMakeReady(makeReady) {
    throw new Error('setMakeReady not yet implemented');
  }

  /**
   * Get the model's make ready items.
   * @returns {Promise<Object|null>} The make ready items.
   */
  async getMakeReadyClearances() {
    return await this.#readDataOnce('models/make_ready/make_ready_items');
  }

  /**
   * Listen for changes to the model's make ready clearances.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the make ready clearances change.
   * @returns {() => void} A function to remove the listener.
   */
  onMakeReadyClearancesSnapshot(callback) {
    return this.#setUpListener('models/make_ready/make_ready_items', callback);
  }

  /**
   * Get the model's proposed cable logic.
   * @returns {Promise<Object|null>} The proposed cable logic.
   */
  async getMakeReadyProposedCableLogic() {
    return await this.#readDataOnce('models/make_ready/proposed_cable_logic');
  }

  /**
   * Listen for changes to the model's proposed cable logic.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the proposed cable logic changes.
   * @returns {() => void} A function to remove the listener.
   */
  onMakeReadyProposedCableLogicSnapshot(callback) {
    return this.#setUpListener('models/make_ready/proposed_cable_logic', callback);
  }

  /**
   * Get the model's virtual rideout settings.
   * @returns {Promise<Object|null>} The virtual rideout settings.
   */
  async getMakeReadyVirtualRideoutSettings() {
    return await this.#readDataOnce('models/make_ready/virtual_rideout/settings');
  }

  /**
   * Get the model's map print annotation logic.
   * @returns {Promise<Object|null>} The model's map print annotation logic, or null if none exists
   */
  async getMapPrintAnnotationLogic() {
    return await this.#readDataOnce('models/map_print_annotation_logic');
  }

  /**
   * Listen for changes to the model's map print annotation logic.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's map print annotation logic changes.
   * @returns {() => void} A function to remove the listener.
   */
  onMapPrintAnnotationLogicSnapshot(callback) {
    return this.#setUpListener('models/map_print_annotation_logic', callback);
  }

  /**
   * Set the model's map print annotation logic.
   * @param {Object|null} mapPrintAnnotationLogic - The map print annotation logic to set for the model.
   * @returns {Promise<void>} A promise that resolves when the map print annotation logic has been set.
   */
  async setMapPrintAnnotationLogic(mapPrintAnnotationLogic) {
    if (mapPrintAnnotationLogic != null && typeof mapPrintAnnotationLogic !== 'object') {
      throw new Error('mapPrintAnnotationLogic must be an object or null');
    }
    const update = composeModelPathUpdate('models/map_print_annotation_logic', mapPrintAnnotationLogic ?? null);
    return await this.reference.update(update);
  }

  /**
   * Create, update, or delete a single map print annotation logic entry.
   * @param {string} key - The key of the entry to set.
   * @param {Object|null} entry - The entry to set (e.g. `{ name, logic }`), or null to delete the entry.
   * @returns {Promise<void>} A promise that resolves when the entry has been set.
   */
  async setMapPrintAnnotationLogicEntry(key, entry) {
    if (typeof key !== 'string' || key === '') throw new Error('key must be a non-empty string');
    if (entry != null && typeof entry !== 'object') throw new Error('entry must be an object or null');
    const update = composeModelPathUpdate(`models/map_print_annotation_logic/${key}`, entry);
    return await this.reference.update(update);
  }

  /**
   * Get the model's map styles.
   * @returns {Promise<Object>} The model's map styles.
   */
  async getMapStyles() {
    return await this.#readDataOnce('models/map_styles');
  }

  /**
   * Listen for changes to the model's map styles.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's map styles change.
   * @returns {() => void} A function to remove the listener.
   */
  onMapStylesSnapshot(callback) {
    return this.#setUpListener('models/map_styles', callback);
  }

  /**
   * Set the model's map styles.
   * @param {Object} mapStyles - The map styles to set for the model.
   * @returns {Promise<void>} A promise that resolves when the map styles have been set.
   */
  async setMapStyles(mapStyles) {
    throw new Error('setMapStyles not yet implemented');
  }

  /**
   * Get a specific map style by key.
   * @param {string} mapStyleKey - The key of the map style to get.
   * @returns {Promise<Object>} The map style.
   */
  async getMapStyle(mapStyleKey) {
    return await this.#readDataOnce(`models/map_styles/${mapStyleKey}`);
  }

  /**
   * Set a specific map style by key.
   * @param {string} mapStyleKey - The key of the map style to set.
   * @param {Object} value - The value to set for the map style.
   * @returns {Promise<void>} A promise that resolves when the map style has been set.
   */
  async setMapStyle(mapStyleKey, value) {
    throw new Error('setMapStyle not yet implemented');
  }

  /**
   * Get the model's mapping buttons.
   * @returns {Promise<Object>} The model's mapping buttons.
   */
  async getMappingButtons() {
    return await this.#readDataOnce('models/mapping_buttons');
  }

  /**
   * Listen for changes to the model's mapping buttons.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's mapping buttons change.
   * @returns {() => void} A function to remove the listener.
   */
  onMappingButtonsSnapshot(callback) {
    return this.#setUpListener('models/mapping_buttons', callback);
  }

  /**
   * Set the model's mapping buttons.
   * @param {Object} mappingButtons - The mapping buttons to set for the model.
   * @returns {Promise<void>} A promise that resolves when the mapping buttons have been set.
   */
  async setMappingButtons(mappingButtons) {
    throw new Error('setMappingButtons not yet implemented');
  }

  /**
   * Get a specific mapping button by key.
   * @param {string} buttonKey - The key of the mapping button to get.
   * @returns {Promise<Object>} The mapping button.
   */
  async getMappingButton(buttonKey) {
    return await this.#readDataOnce(`models/mapping_buttons/${buttonKey}`);
  }

  /**
   * Set a specific mapping button by key.
   * @param {string} buttonKey - The key of the mapping button to set.
   * @param {Object} value - The value to set for the mapping button.
   * @returns {Promise<void>} A promise that resolves when the mapping button has been set.
   */
  async setMappingButton(buttonKey, value) {
    throw new Error('setMappingButton not yet implemented');
  }

  /**
   * Get the model's MR preferences.
   * @returns {Promise<Object>} The model's MR preferences.
   */
  async getMrPreferences() {
    return await this.#readDataOnce('models/mr_preferences');
  }

  /**
   * Listen for changes to the model's MR preferences.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's MR preferences change.
   * @returns {() => void} A function to remove the listener.
   */
  onMrPreferencesSnapshot(callback) {
    return this.#setUpListener('models/mr_preferences', callback);
  }

  /**
   * Set the model's MR preferences.
   * @param {Object} mrPreferences - The MR preferences to set for the model.
   * @returns {Promise<void>} A promise that resolves when the MR preferences have been set.
   */
  async setMrPreferences(mrPreferences) {
    throw new Error('setMrPreferences not yet implemented');
  }

  /**
   * Get the model's NJUNS models.
   * @returns {Promise<Object>} The model's NJUNS models.
   */
  async getNjunsModels() {
    return await this.#readDataOnce('models/njuns_models');
  }

  /**
   * Listen for changes to the model's NJUNS models.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's NJUNS models change.
   * @returns {() => void} A function to remove the listener.
   */
  onNjunsModelsSnapshot(callback) {
    return this.#setUpListener('models/njuns_models', callback);
  }

  /**
   * Set the model's NJUNS models.
   * @param {Object} njunsModels - The NJUNS models to set for the model.
   * @returns {Promise<void>} A promise that resolves when the NJUNS models have been set.
   */
  async setNjunsModels(njunsModels) {
    throw new Error('setNjunsModels not yet implemented');
  }

  /**
   * Get the model's overhead time allocations.
   * @returns {Promise<Object>} The model's overhead time allocations.
   */
  async getOverheadTimeAllocations() {
    return await this.#readDataOnce('models/overhead_time_allocations');
  }

  /**
   * Listen for changes to the model's overhead time allocations.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's overhead time allocations change.
   * @returns {() => void} A function to remove the listener.
   */
  onOverheadTimeAllocationsSnapshot(callback) {
    return this.#setUpListener('models/overhead_time_allocations', callback);
  }

  /**
   * Set the model's overhead time allocations.
   * @param {Object} overheadTimeAllocations - The overhead time allocations to set for the model.
   * @returns {Promise<void>} A promise that resolves when the overhead time allocations have been set.
   */
  async setOverheadTimeAllocations(overheadTimeAllocations) {
    throw new Error('setOverheadTimeAllocations not yet implemented');
  }

  /**
   * Get the model's pole loading configuration.
   * @returns {Promise<Object>} The model's pole loading configuration.
   */
  async getPoleLoading() {
    return await this.#readDataOnce('models/pole_loading');
  }

  /**
   * Listen for changes to the model's pole loading configuration.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's pole loading configuration changes.
   * @returns {() => void} A function to remove the listener.
   */
  onPoleLoadingSnapshot(callback) {
    return this.#setUpListener('models/pole_loading', callback);
  }

  /**
   * Set the model's pole loading configuration.
   * @param {Object} poleLoading - The pole loading configuration to set for the model.
   * @returns {Promise<void>} A promise that resolves when the pole loading configuration has been set.
   */
  async setPoleLoading(poleLoading) {
    throw new Error('setPoleLoading not yet implemented');
  }

  /**
   * Get the model's pole loading specs.
   * @returns {Promise<Object|null>} The pole loading specs.
   */
  async getPoleLoadingSpecs() {
    return await this.#readDataOnce('models/pole_loading/specs');
  }

  /**
   * Get the model's pole loading wire specs.
   * @returns {Promise<Object|null>} The wire specs.
   */
  async getPoleLoadingWireSpecs() {
    return await this.#readDataOnce('models/pole_loading/specs/wire_specs');
  }

  /**
   * Get the model's pole loading pole specs.
   * @returns {Promise<Object|null>} The pole specs.
   */
  async getPoleLoadingPoleSpecs() {
    return await this.#readDataOnce('models/pole_loading/specs/pole_specs');
  }

  /**
   * Get the model's pole loading insulator specs.
   * @returns {Promise<Object|null>} The insulator specs.
   */
  async getPoleLoadingInsulatorSpecs() {
    return await this.#readDataOnce('models/pole_loading/specs/insulator_specs');
  }

  /**
   * Get the model's pole loading tension districts.
   * @returns {Promise<Object|null>} The tension districts.
   */
  async getPoleLoadingTensionDistricts() {
    return await this.#readDataOnce('models/pole_loading/tension_districts');
  }

  /**
   * Get the model's pole loading load cases.
   * @returns {Promise<Object|null>} The load cases.
   */
  async getPoleLoadingLoadCases() {
    return await this.#readDataOnce('models/pole_loading/load_cases');
  }

  /**
   * Get the model's pole loading defaults.
   * @returns {Promise<Object|null>} The pole loading defaults (engine_version, bulk_loading, spida, ocalc, etc.).
   */
  async getPoleLoadingDefaults() {
    return await this.#readDataOnce('models/pole_loading/defaults');
  }

  /**
   * Get the model's pole replacement dialog configuration.
   * @returns {Promise<Object>} The model's pole replacement dialog configuration.
   */
  async getPoleReplacementDialog() {
    return await this.#readDataOnce('models/pole_replacement_dialog');
  }

  /**
   * Listen for changes to the model's pole replacement dialog configuration.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's pole replacement dialog configuration changes.
   * @returns {() => void} A function to remove the listener.
   */
  onPoleReplacementDialogSnapshot(callback) {
    return this.#setUpListener('models/pole_replacement_dialog', callback);
  }

  /**
   * Set the model's pole replacement dialog configuration.
   * @param {Object} poleReplacementDialog - The pole replacement dialog configuration to set for the model.
   * @returns {Promise<void>} A promise that resolves when the pole replacement dialog configuration has been set.
   */
  async setPoleReplacementDialog(poleReplacementDialog) {
    throw new Error('setPoleReplacementDialog not yet implemented');
  }

  /**
   * Get the model's power specs.
   * @returns {Promise<Object>} The model's power specs.
   */
  async getPowerSpecs() {
    return await this.#readDataOnce('models/power_specs');
  }

  /**
   * Listen for changes to the model's power specs.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's power specs change.
   * @returns {() => void} A function to remove the listener.
   */
  onPowerSpecsSnapshot(callback) {
    return this.#setUpListener('models/power_specs', callback);
  }

  /**
   * Set the model's power specs.
   * @param {Object} powerSpecs - The power specs to set for the model.
   * @returns {Promise<void>} A promise that resolves when the power specs have been set.
   */
  async setPowerSpecs(powerSpecs) {
    throw new Error('setPowerSpecs not yet implemented');
  }

  /**
   * Get the model's project config.
   * @returns {Promise<Object>} The model's project config.
   */
  async getProjectConfig() {
    return await this.#readDataOnce('models/project_config');
  }

  /**
   * Listen for changes to the model's project config.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's project config changes.
   * @returns {() => void} A function to remove the listener.
   */
  onProjectConfigSnapshot(callback) {
    return this.#setUpListener('models/project_config', callback);
  }

  /**
   * Set the model's project config.
   * @param {Object} projectConfig - The project config to set for the model.
   * @returns {Promise<void>} A promise that resolves when the project config has been set.
   */
  async setProjectConfig(projectConfig) {
    throw new Error('setProjectConfig not yet implemented');
  }

  /**
   * Get the model's proposed sag data.
   * @returns {Promise<Object>} The model's proposed sag data.
   */
  async getProposedSagData() {
    return await this.#readDataOnce('models/proposed_sag_data');
  }

  /**
   * Listen for changes to the model's proposed sag data.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's proposed sag data changes.
   * @returns {() => void} A function to remove the listener.
   */
  onProposedSagDataSnapshot(callback) {
    return this.#setUpListener('models/proposed_sag_data', callback);
  }

  /**
   * Set the model's proposed sag data.
   * @param {Object} proposedSagData - The proposed sag data to set for the model.
   * @returns {Promise<void>} A promise that resolves when the proposed sag data has been set.
   */
  async setProposedSagData(proposedSagData) {
    throw new Error('setProposedSagData not yet implemented');
  }

  /**
   * Get the model's QC data.
   * @returns {Promise<Object>} The model's QC data.
   */
  async getQc() {
    return await this.#readDataOnce('models/qc');
  }

  /**
   * Listen for changes to the model's QC data.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's QC data changes.
   * @returns {() => void} A function to remove the listener.
   */
  onQcSnapshot(callback) {
    return this.#setUpListener('models/qc', callback);
  }

  /**
   * Set the model's QC data.
   * @param {Object} qc - The QC data to set for the model.
   * @returns {Promise<void>} A promise that resolves when the QC data has been set.
   */
  async setQc(qc) {
    throw new Error('setQc not yet implemented');
  }

  /**
   * Get the model's quality control.
   * @returns {Promise<Object>} The model's quality control.
   */
  async getQualityControl() {
    return await this.#readDataOnce('models/quality_control');
  }

  /**
   * Listen for changes to the model's quality control.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's quality control changes.
   * @returns {() => void} A function to remove the listener.
   */
  onQualityControlSnapshot(callback) {
    return this.#setUpListener('models/quality_control', callback);
  }

  /**
   * Set the model's quality control.
   * @param {Object} qualityControl - The quality control to set for the model.
   * @returns {Promise<void>} A promise that resolves when the quality control has been set.
   */
  async setQualityControl(qualityControl) {
    throw new Error('setQualityControl not yet implemented');
  }

  /**
   * Get the model's review sampling.
   * @returns {Promise<Object>} The model's review sampling.
   */
  async getReviewSampling() {
    return await this.#readDataOnce('models/review_sampling');
  }

  /**
   * Listen for changes to the model's review sampling.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's review sampling changes.
   * @returns {() => void} A function to remove the listener.
   */
  onReviewSamplingSnapshot(callback) {
    return this.#setUpListener('models/review_sampling', callback);
  }

  /**
   * Set the model's review sampling.
   * @param {Object} reviewSampling - The review sampling to set for the model.
   * @returns {Promise<void>} A promise that resolves when the review sampling has been set.
   */
  async setReviewSampling(reviewSampling) {
    throw new Error('setReviewSampling not yet implemented');
  }

  /**
   * Get the model's roles.
   * @returns {Promise<Object>} The model's roles.
   */
  async getRoles() {
    return await this.#readDataOnce('models/roles');
  }

  /**
   * Listen for changes to the model's roles.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's roles change.
   * @returns {() => void} A function to remove the listener.
   */
  onRolesSnapshot(callback) {
    return this.#setUpListener('models/roles', callback);
  }

  /**
   * Set the model's roles.
   * @param {Object} roles - The roles to set for the model.
   * @returns {Promise<void>} A promise that resolves when the roles have been set.
   */
  async setRoles(roles) {
    throw new Error('setRoles not yet implemented');
  }

  /**
   * Get the model's routines.
   * @returns {Promise<Object>} The model's routines.
   */
  async getRoutines() {
    return await this.#readDataOnce('models/routines');
  }

  /**
   * Listen for changes to the model's routines.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's routines change.
   * @returns {() => void} A function to remove the listener.
   */
  onRoutinesSnapshot(callback) {
    return this.#setUpListener('models/routines', callback);
  }

  /**
   * Set the model's routines.
   * @param {Object} routines - The routines to set for the model.
   * @returns {Promise<void>} A promise that resolves when the routines have been set.
   */
  async setRoutines(routines) {
    throw new Error('setRoutines not yet implemented');
  }

  /**
   * Get the model's sag data.
   * @returns {Promise<Object>} The model's sag data.
   */
  async getSagData() {
    return await this.#readDataOnce('models/sag_data');
  }

  /**
   * Listen for changes to the model's sag data.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's sag data changes.
   * @returns {() => void} A function to remove the listener.
   */
  onSagDataSnapshot(callback) {
    return this.#setUpListener('models/sag_data', callback);
  }

  /**
   * Set the model's sag data.
   * @param {Object} sagData - The sag data to set for the model.
   * @returns {Promise<void>} A promise that resolves when the sag data has been set.
   */
  async setSagData(sagData) {
    throw new Error('setSagData not yet implemented');
  }

  /**
   * Get the model's saved views.
   * @returns {Promise<Object>} The model's saved views.
   */
  async getSavedViews() {
    return await this.#readDataOnce('models/saved_views');
  }

  /**
   * Listen for changes to the model's saved views.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's saved views change.
   * @returns {() => void} A function to remove the listener.
   */
  onSavedViewsSnapshot(callback) {
    return this.#setUpListener('models/saved_views', callback);
  }

  /**
   * Set the model's saved views.
   * @param {Object} savedViews - The saved views to set for the model.
   * @returns {Promise<void>} A promise that resolves when the saved views have been set.
   */
  async setSavedViews(savedViews) {
    throw new Error('setSavedViews not yet implemented');
  }

  /**
   * Get a specific saved view by key.
   * @param {string} key - The saved view key.
   * @returns {Promise<Object|null>} The saved view.
   */
  async getSavedView(key) {
    return await this.#readDataOnce(`models/saved_views/${key}`);
  }

  /**
   * Listen for changes to a specific saved view by key.
   * @param {string} key - The saved view key.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the saved view changes.
   * @returns {() => void} A function to remove the listener.
   */
  onSavedViewSnapshot(key, callback) {
    return this.#setUpListener(`models/saved_views/${key}`, callback);
  }

  /**
   * Get the model's shortcuts.
   * @returns {Promise<Object>} The model's shortcuts.
   */
  async getShortcuts() {
    return await this.#readDataOnce('models/shortcuts');
  }

  /**
   * Listen for changes to the model's shortcuts.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's shortcuts change.
   * @returns {() => void} A function to remove the listener.
   */
  onShortcutsSnapshot(callback) {
    return this.#setUpListener('models/shortcuts', callback);
  }

  /**
   * Set the model's shortcuts.
   * @param {Object} shortcuts - The shortcuts to set for the model.
   * @returns {Promise<void>} A promise that resolves when the shortcuts have been set.
   */
  async setShortcuts(shortcuts) {
    throw new Error('setShortcuts not yet implemented');
  }

  /**
   * Get the model's snapshot models.
   * @returns {Promise<Object>} The model's snapshot models.
   */
  async getSnapshotModels() {
    return await this.#readDataOnce('models/snapshot_models');
  }

  /**
   * Listen for changes to the model's snapshot models.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's snapshot models change.
   * @returns {() => void} A function to remove the listener.
   */
  onSnapshotModelsSnapshot(callback) {
    return this.#setUpListener('models/snapshot_models', callback);
  }

  /**
   * Set the model's snapshot models.
   * @param {Object} snapshotModels - The snapshot models to set for the model.
   * @returns {Promise<void>} A promise that resolves when the snapshot models have been set.
   */
  async setSnapshotModels(snapshotModels) {
    throw new Error('setSnapshotModels not yet implemented');
  }

  /**
   * Get the model's tags.
   * @returns {Promise<Object>} The model's tags.
   */
  async getTags() {
    return await this.#readDataOnce('models/tags');
  }

  /**
   * Listen for changes to the model's tags.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's tags change.
   * @returns {() => void} A function to remove the listener.
   */
  onTagsSnapshot(callback) {
    return this.#setUpListener('models/tags', callback);
  }

  /**
   * Set the model's tags.
   * @param {Object} tags - The tags to set for the model.
   * @returns {Promise<void>} A promise that resolves when the tags have been set.
   */
  async setTags(tags) {
    throw new Error('setTags not yet implemented');
  }

  /**
   * Get the model's trace models.
   * @returns {Promise<Object>} The model's trace models.
   */
  async getTraceModels() {
    return await this.#readDataOnce('models/trace_models');
  }

  /**
   * Listen for changes to the model's trace models.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's trace models change.
   * @returns {() => void} A function to remove the listener.
   */
  onTraceModelsSnapshot(callback) {
    return this.#setUpListener('models/trace_models', callback);
  }

  /**
   * Set the model's trace models.
   * @param {Object} traceModels - The trace models to set for the model.
   * @returns {Promise<void>} A promise that resolves when the trace models have been set.
   */
  async setTraceModels(traceModels) {
    throw new Error('setTraceModels not yet implemented');
  }

  /**
   * Get the model's workflow timelines.
   * @returns {Promise<Object>} The model's workflow timelines.
   */
  async getWorkflowTimelines() {
    return await this.#readDataOnce('portal_config/timeline_models');
  }

  /**
   * Listen for changes to the model's workflow timelines.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the model's workflow timelines change.
   * @returns {() => void} A function to remove the listener.
   */
  onWorkflowTimelinesSnapshot(callback) {
    return this.#setUpListener('portal_config/timeline_models', callback);
  }

  /**
   * Get the data at a specific path in the model.
   * @param {string} path - The path to read (relative to `this.reference`).
   * @returns {Promise<any>} The data at the path.
   */
  async getModelPath(path) {
    return await this.#readDataOnce(path);
  }

  /**
   * Listen for changes to a specific path in the model.
   * @param {string} path - The path to listen to (relative to `this.reference`).
   * @param {OnSnapshotCallback} callback - The callback function to be called when the data at the path changes.
   * @returns {() => void} A function to remove the listener.
   */
  onModelPathSnapshot(path, callback) {
    return this.#setUpListener(path, callback);
  }

  /**
   * Set the data at a specific path in the model.
   * @param {string} path - The path to set (relative to `this.reference`).
   * @param {any} value - The value to set at the path.
   * @returns {Promise<void>} A promise that resolves when the data has been set.
   */
  async setModelPath(path, value) {
    throw new Error('setModelPath not yet implemented');
  }

  /**
   * Set up a no-op listener to cache job data in memory (does nothing if the listener has already been set up).
   * @param {string} path - The path to listen to.
   */
  #setUpCachingListener(path) {
    // Do nothing if the listener has already been set up.
    if (this.#cachingListenersByPath[path]) return;

    // Do nothing if there is a caching listener at a shallower path.
    const pathParts = path.split('/').slice(0, -1);
    for (const index in pathParts) {
      const parentPath = pathParts.slice(0, Number(index) + 1).join('/');
      if (this.#cachingListenersByPath[parentPath]) return;
    }

    // Set up the no-op listener.
    const childRef = this.reference.child(path);
    const listener = childRef.on('value', () => {});
    this.#cachingListenersByPath[path] = listener;

    // If there are caching listeners on a child path of this path, remove them.
    const childPathsWithListeners = Object.keys(this.#cachingListenersByPath).filter((childPath) => childPath.startsWith(`${path}/`));
    for (const childPath of childPathsWithListeners) {
      this.reference.child(childPath).off('value', this.#cachingListenersByPath[childPath]);
      delete this.#cachingListenersByPath[childPath];
    }
  }

  /**
   * Sets up a listener for a child path.
   * @param {string} childPath - The child path to listen to.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the child path changes.
   * @returns {() => void} A function to remove the listener.
   */
  #setUpListener(childPath, callback) {
    // If desired, cache job data for this path.
    if (this.#autoCache) this.#setUpCachingListener(childPath);

    // Set up the specified listener.
    const childRef = this.reference.child(childPath);
    childRef.on('value', callback);

    // Keep a record of the listener, and return a function to remove it.
    this.#listenersByPath[childPath] ??= [];
    this.#listenersByPath[childPath].push(callback);
    const listenerUnsubscriber = () => {
      childRef.off('value', callback);
      this.#listenersByPath[childPath] = this.#listenersByPath[childPath].filter((listener) => listener !== callback);
    };

    return listenerUnsubscriber;
  }

  /**
   * Reads data from a path once (uses data from other listeners if they are available).
   * @param {string} path - The path to read from.
   * @param {Object} [options] - Options for reading the data.
   * @param {boolean} [options.absolutePath] - If true, `path` is treated as an absolute database path rather than
   * relative to `this.reference`.
   * @returns {Promise<any>} The data at the path.
   */
  async #readDataOnce(path, options) {
    // If the path is absolute, create a new reference directly from the database.
    if (options?.absolutePath) {
      const snapshot = await this.database
        .ref(path)
        .once('value')
        .catch((error) => {
          const { message } = error;
          throw new Error(message);
        });
      return snapshot.val();
    }

    // If desired, cache job data for this path.
    if (this.#autoCache) this.#setUpCachingListener(path);

    // Read the data from the path.
    const snapshot = await this.reference
      .child(path)
      .once('value')
      .catch((error) => {
        const { message } = error;
        throw new Error(message);
      });
    return snapshot.val();
  }
}
